import { CreditCardOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { BillOut } from "../../../../../api/bills";
import BusyRing from "../../../../../components/BusyRing";
import { SCHEMA_BILL_STRIP_TONES, type SchemaBillStripTone } from "./schemaBillStripTones";

/**
 * Плочката на сметка в лентите под схемата: ширината следва името („1011" е
 * по-къса от „До вратата"), а височината, отстъпите и шрифтовете са еднакви в
 * двете ленти; името и сумата с „€", в цвета на лентата. „Карта — без бон" —
 * иконка с подсказка. Закриващата се — приглушено име, неактивна, със спинер
 * и „Закрива се". Сметка на друг сервитьор (обща номерация) — бледа, но
 * кликаема, като масата ѝ на схемата.
 */
export const SchemaBillTile = ({
  tone,
  bill,
  selected,
  closing,
  faded,
  onSelect,
  onOpen,
  tileRef,
}: {
  tone: SchemaBillStripTone;
  bill: BillOut;
  selected: boolean;
  closing: boolean;
  faded: boolean;
  onSelect: () => void;
  onOpen?: () => void;
  tileRef: (element: HTMLButtonElement | null) => void;
}) => {
  const { t } = useTranslation();
  const tones = SCHEMA_BILL_STRIP_TONES[tone];
  const cardPending = Boolean(bill.card_payment_pending);
  return (
    <button
      ref={tileRef}
      type="button"
      title={cardPending ? `${bill.name} · ${t("ordering.cardNoReceiptTag")}` : bill.name}
      disabled={closing}
      onClick={onSelect}
      onDoubleClick={onOpen}
      className={`flex h-8 min-w-[72px] max-w-[220px] shrink-0 items-center rounded-[9px] px-3 transition-[transform,opacity] duration-150 ${
        closing ? tones.closingTile : tones.tile
      } ${selected ? tones.selectedRing : ""} ${faded && !selected ? "opacity-45" : ""}`}
    >
      {/* Името и сумата — на една базова линия (различни шрифт и размер),
          с тънка черта между тях. */}
      <span className="flex min-w-0 flex-1 items-baseline gap-2">
        <span
          className={`min-w-0 truncate text-[15px] font-extrabold leading-[1.2] tracking-[-0.2px] ${
            closing ? "opacity-35" : ""
          }`}
        >
          {bill.name}
        </span>
        <span aria-hidden className="h-3.5 w-px shrink-0 self-center bg-current opacity-35" />
        {closing ? (
          <span className="flex shrink-0 items-center gap-1 self-center whitespace-nowrap text-[11px] font-bold leading-none text-ink">
            <BusyRing className="h-3 w-3" strokeWidth={11} arcClass="stroke-brand" trackClass="stroke-brand/25" />
            {t("ordering.tableClosing")}
          </span>
        ) : (
          <span className="flex shrink-0 items-baseline gap-1">
            {cardPending && (
              // `!` — `.anticon { color: inherit }` на antd е извън Tailwind layer-ите.
              <CreditCardOutlined className="self-center text-[12px] !text-pos-disc-ink" />
            )}
            {/* Сумата като на картата в списъка (`total_amount`; `total` на
                нефискализираната е 0), с „€". */}
            <span className="whitespace-nowrap font-pos-mono text-[13px] font-semibold leading-none">
              {parseFloat(bill.total_amount).toFixed(2)} €
            </span>
          </span>
        )}
      </span>
    </button>
  );
};
